const CDP_TRUSTED_ORIGIN = 'https://www.barstoolsports.com';
const CDP_MAX_ID_LENGTH = 256;

const isValidCDPId = value =>
  typeof value === 'string' &&
  value.trim() !== '' &&
  value.length <= CDP_MAX_ID_LENGTH;

/**
 * Validates a postMessage event and extracts CDP identity fields.
 * Returns null unless the origin is an exact match for the trusted Barstool origin
 * and the payload is a well-formed object with string identifiers.
 *
 * @param {MessageEvent} event
 * @returns {{ anonymousID: (string|null), barstoolUserID: (string|null) } | null}
 */
const parseCDPIdentityMessage = event => {
  if (!event || event.origin !== CDP_TRUSTED_ORIGIN) return null;

  let data = event.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const hasAnonymousID = Object.prototype.hasOwnProperty.call(data, 'ajs_anonymous_id');
  const hasUserID = Object.prototype.hasOwnProperty.call(data, 'ajs_user_id');
  if (!hasAnonymousID && !hasUserID) return null;

  // A present-but-malformed field invalidates the whole message.
  if (hasAnonymousID && !isValidCDPId(data.ajs_anonymous_id)) return null;
  if (hasUserID && data.ajs_user_id !== null && !isValidCDPId(data.ajs_user_id)) return null;

  const anonymousID = hasAnonymousID ? data.ajs_anonymous_id : null;
  const barstoolUserID = hasUserID ? data.ajs_user_id : null;
  if (!anonymousID && !barstoolUserID) return null;

  return { anonymousID, barstoolUserID };
};

/**
 * Receives identity messages from the trusted Barstool origin and configures `whoamiSDK`
 * with the anonymous ID, Barstool user ID and, if logged in, the Shopify customer ID.
 * Runs once: after a successful setup it flags localStorage and removes itself.
 *
 * @param {MessageEvent} event - The message event object received by the event listener.
 */
const handleMessage = event => {
  const identity = parseCDPIdentityMessage(event);
  if (!identity) return;

  const whoamiSDK = window.whoamiSDK;
  if (!whoamiSDK) return;

  if (identity.anonymousID) {
    whoamiSDK.setAnonymousID(identity.anonymousID);
  }

  if (identity.barstoolUserID) {
    whoamiSDK.identify({
      barstoolUserID: identity.barstoolUserID
    });
  }

  const shopifyCustomerID = window.__st && window.__st.cid;
  if (shopifyCustomerID !== undefined && shopifyCustomerID !== null) {
    whoamiSDK.identify({
      shopifyCustomerID: String(shopifyCustomerID)
    });
  }

  localStorage.setItem('handleMessageRan', 'true');
  window.removeEventListener('message', handleMessage);
};

const WHOAMI_SDK_URL = 'https://whoami-static.barstoolsports.com/prod/0.3.1/iife.min.js';
const CDP_BRIDGE_URL = 'https://barstoolsports.com/bridge.html';

// CDP calls made before the SDK is loaded wait here; they are dropped if analytics consent never arrives.
const cdpQueue = [];
let cdpReady = false;

const withWhoami = fn => {
  if (!cdpReady) {
    cdpQueue.push(fn);
    return;
  }
  if (window.whoamiSDK) fn(window.whoamiSDK);
};

/**
 * Starts CDP tracking once the whoami SDK has loaded: flushes queued events, sends the page view,
 * and opens the barstoolsports.com identity bridge.
 */
function initCDP() {
  if (cdpReady) return;
  cdpReady = true;

  if (!window.whoamiSDK) {
    console.error('whoamiSDK is not available after loading.');
    cdpQueue.length = 0;
    return;
  }

  cdpQueue.splice(0).forEach(fn => fn(window.whoamiSDK));
  if (typeof window.whoamiSDK.page === 'function') window.whoamiSDK.page();

  if (!localStorage.getItem('handleMessageRan')) {
    window.addEventListener('message', handleMessage);
    const bridge = document.createElement('iframe');
    bridge.src = CDP_BRIDGE_URL;
    bridge.className = 'hide';
    bridge.tabIndex = -1;
    bridge.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bridge);
  }
}

if (window.BSSConsent) {
  window.BSSConsent.whenGranted('analytics', () => {
    window.BSSConsent.loadScript(WHOAMI_SDK_URL)
      .then(() => {
        if (document.body) initCDP();
        else document.addEventListener('DOMContentLoaded', initCDP);
      })
      .catch(error => console.error('Failed to load whoami SDK:', error));
  });
}

/**
 * Sends a 'ProductViewed' event to the whoamiSDK tracking system for a given product. This function
 * is designed to be called when a product is viewed by the user, capturing essential details about the
 * product such as its Shopify product ID, variant ID, vendor name, and price. The variant ID is determined
 * either from the URL query parameter 'variant' or the provided product object's `shopifyVariantID` field.
 * This allows for tracking both direct product page views and views through product modals or similar elements.
 *
 * @param {Object} product - An object containing details of the viewed product.
 * @param {string} product.shopifyProductID - The Shopify product ID.
 * @param {string} [product.shopifyVariantID] - The Shopify variant ID, optional if 'variant' query parameter is used.
 * @param {string} [product.shopifyVendorName] - The name of the product's vendor.
 * @param {number} product.price - The price of the product.
 */
function sendCDPProductViewedEvent(product) {
  if (!product) return;
  const searchParams = new URLSearchParams(window.location.search);
  // variantId handles pdp views OR product-modal views
  const variantId = searchParams.get('variant') || product.shopifyVariantID;
  withWhoami(whoamiSDK =>
    whoamiSDK.track('ProductViewed', {
      product: {
        shopifyProductID: product.shopifyProductID,
        shopifyVariantID: variantId,
        shopifyVendorName: product.shopifyVendorName,
        price: Number(product.price)
      }
    })
  );
}

/**
 * Sets hidden cart attributes for CDP (Customer Data Platform) IDs based on the `whoamiSDK` object's properties.
 */
function setCDPCartAttributes() {
  withWhoami(whoamiSDK => {
    const anonymousIdCartInput = document.getElementById('cdp-anon-id');
    const barstoolIdCartInput = document.getElementById('cdp-barstool-id');

    if (
      anonymousIdCartInput &&
      anonymousIdCartInput.value === '' &&
      whoamiSDK.anonymousID
    ) {
      anonymousIdCartInput.value = whoamiSDK.anonymousID;
    }
    if (
      barstoolIdCartInput &&
      barstoolIdCartInput.value === '' &&
      whoamiSDK.barstoolUserID
    ) {
      barstoolIdCartInput.value = whoamiSDK.barstoolUserID;
    }
  });
}

/**
 * Sends a 'ProductAddedToCart' tracking event to the whoamiSDK with detailed cart and product information.
 * The cart object must contain an array of items, each representing a product added to the cart, including
 * product ID, variant ID, vendor name, price, and quantity. Additional cart details such as the cart token,
 * total quantity, total price, and currency code are also included in the event.
 *
 * @param {Object} cart - The cart object containing items and additional cart information.
 * @param {Array} cart.items - An array of cart item objects.
 * @param {string} cart.token - A unique identifier for the cart.
 * @param {number} cart.item_count - The total number of items in the cart.
 * @param {number} cart.total_price - The total price of all items in the cart.
 * @param {string} cart.currency - The ISO currency code for the cart's total price.
 */
function sendCDPAddToCartEvent(cart) {
  if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;
  const cartProducts = cart.items.map(item => ({
    shopifyProductID: String(item.product_id),
    shopifyVariantID: String(item.variant_id),
    shopifyVendorName: item.vendor,
    price: Number(item.price),
    quantity: Number(item.quantity)
  }));
  withWhoami(whoamiSDK =>
    whoamiSDK.track('ProductAddedToCart', {
      product: {
        shopifyProductID: String(cart.items[0].product_id),
        shopifyVariantID: String(cart.items[0].variant_id),
        shopifyVendorName: cart.items[0].vendor,
        price: Number(cart.items[0].price),
        quantity: Number(cart.items[0].quantity)
      },
      cart: {
        shopifyCartToken: String(cart.token),
        products: cartProducts,
        totalQuantity: Number(cart.item_count),
        totalPrice: Number(cart.total_price),
        currencyIsoCode: String(cart.currency)
      }
    })
  );
}
/*
 * Disabled: `rebuy.add` only fires for Rebuy's native widgets (pop-ups, Smart Cart, or
 * Re-enable if native Rebuy widgets are turned on.
 *
 * function handleCDPRebuyCartUpdate() {
 *   const cart = window.Rebuy && window.Rebuy.Cart;
 *   if (cart) {
 *     cart.fetchShopifyCart(cart => sendCDPAddToCartEvent(cart));
 *   }
 *   setCDPCartAttributes();
 * }
 *
 * document.addEventListener('rebuy.add', handleCDPRebuyCartUpdate);
 */
